import React from 'react';
import { GridColumn } from '../../../types/datagrid';
import { InlineEditor } from './InlineEditor';

interface GridCellProps {
  value: any;
  column: GridColumn;
  columnIndex: number;
  rowData: any;
  rowId: string;
  isEditing?: boolean;
  editingValue?: any;
  compactDensity?: boolean;
  onEdit?: (columnId: string, value: any) => Promise<void>;
  permissions?: {
    canEdit?: boolean;
  };
}

export const GridCell: React.FC<GridCellProps> = ({
  value,
  column,
  columnIndex,
  rowData,
  isEditing = false,
  editingValue,
  onEdit,
  permissions = {},
}) => {
  const formatValue = (value: any, column: GridColumn): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400 italic">—</span>;
    }

    switch (column.type) {
      case 'currency': {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return '—';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(numValue);
      }

      case 'number': {
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        return num.toLocaleString();
      }

      case 'date': {
        const date = new Date(value);
        if (isNaN(date.getTime())) return value;
        return date.toLocaleDateString();
      }

      case 'datetime': {
        const datetime = new Date(value);
        if (isNaN(datetime.getTime())) return value;
        return datetime.toLocaleString();
      }

      case 'boolean':
        return (
          <span
            className={`
            inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
            ${value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
          `}
          >
            {value ? 'Yes' : 'No'}
          </span>
        );

      case 'status': {
        const statusColors = {
          active: 'bg-green-100 text-green-800',
          inactive: 'bg-gray-100 text-gray-800',
          pending: 'bg-yellow-100 text-yellow-800',
          completed: 'bg-blue-100 text-blue-800',
          cancelled: 'bg-red-100 text-red-800',
          planning: 'bg-purple-100 text-purple-800',
          on_hold: 'bg-orange-100 text-orange-800',
        };

        const statusColor =
          statusColors[value as keyof typeof statusColors] || 'bg-gray-100 text-gray-800';

        return (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}
          >
            {typeof value === 'string' ? value.replace(/_/g, ' ').toUpperCase() : value}
          </span>
        );
      }

      case 'select': {
        const option = column.options?.find(opt => opt.value === value);
        return option ? (
          <span className="inline-flex items-center space-x-1">
            {option.color && (
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: option.color }} />
            )}
            <span>{option.label}</span>
          </span>
        ) : (
          value
        );
      }

      case 'multiselect': {
        const values = Array.isArray(value) ? value : [value];
        return (
          <div className="flex flex-wrap gap-1">
            {values.slice(0, 2).map((val, idx) => {
              const option = column.options?.find(opt => opt.value === val);
              return (
                <span
                  key={idx}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800"
                >
                  {option?.label || val}
                </span>
              );
            })}
            {values.length > 2 && (
              <span className="text-xs text-gray-500">+{values.length - 2}</span>
            )}
          </div>
        );
      }

      case 'user':
        if (typeof value === 'object' && value.name) {
          return (
            <div className="flex items-center space-x-2">
              {value.avatar && <img src={value.avatar} alt="" className="h-6 w-6 rounded-full" />}
              <span className="truncate">{value.name}</span>
            </div>
          );
        }
        return value;

      case 'link':
        return (
          <a
            href={typeof value === 'object' ? value.url : value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-600 hover:text-brand-700 underline"
            onClick={e => e.stopPropagation()}
          >
            {typeof value === 'object' ? value.text || value.url : value}
          </a>
        );

      case 'calculated':
        // Handle formula errors
        if (typeof value === 'object' && value.error) {
          return (
            <span className="text-red-600 font-mono text-xs" title={value.error.message}>
              #{value.error.type}!
            </span>
          );
        }
        // Format calculated result based on the expected output type
        return formatValue(value, { ...column, type: 'text' });

      case 'text':
      default: {
        const stringValue = String(value);
        const isTruncated = stringValue.length > 50;

        return (
          <span
            className={`${isTruncated ? 'truncate' : ''} block`}
            title={isTruncated ? stringValue : undefined}
          >
            {stringValue}
          </span>
        );
      }
    }
  };

  const canEdit = column.isEditable && onEdit && permissions.canEdit;

  const handleDoubleClick = (e: React.MouseEvent) => {
    if (canEdit) {
      e.stopPropagation();
      // Start editing would be handled by parent component
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (canEdit && (e.key === 'Enter' || e.key === 'F2')) {
      e.stopPropagation();
      // Start editing would be handled by parent component
    }
  };

  return (
    <div
      className={`
        flex items-center border-r border-gray-200 last:border-r-0 px-3
        ${canEdit ? 'hover:bg-blue-50 cursor-pointer' : ''}
        ${isEditing ? 'bg-blue-100' : ''}
      `}
      style={{
        width: column.width || 150,
        minWidth: column.minWidth,
        maxWidth: column.maxWidth,
      }}
      role="gridcell"
      aria-colindex={columnIndex + 1}
      tabIndex={canEdit ? 0 : -1}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
    >
      {isEditing ? (
        <InlineEditor
          value={editingValue !== undefined ? editingValue : value}
          column={column}
          rowData={rowData}
          onSave={async newValue => {
            await onEdit!(column.id, newValue);
          }}
          onCancel={() => {
            // Cancel would be handled by parent component
          }}
        />
      ) : (
        <div className="flex-1 min-w-0 py-1">{formatValue(value, column)}</div>
      )}

      {/* Edit Indicator */}
      {canEdit && !isEditing && (
        <div className="opacity-0 group-hover:opacity-100 ml-2 transition-opacity">
          <svg
            className="h-3 w-3 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
        </div>
      )}
    </div>
  );
};
